/**
 * 父页面消息的运行时校验（设计 §8.1），由 **Tickbird iframe 侧**使用。
 *
 * TypeScript 类型在跨源消息面前等于没有：`event.data` 是对方完全可控的值。
 * 任何不符合契约的输入都返回 null，由调用方静默丢弃。
 */
import { LOCALES, PARENT_MESSAGE, MAX_ENTITY_ID_LENGTH, MAX_ENTITY_TYPE_LENGTH, MAX_ENUM_LENGTH, MAX_IDENTIFIER_LENGTH, MAX_PATH_LENGTH, MAX_PROJECT_CODE_LENGTH, MAX_SYSTEM_CODE_LENGTH, MAX_TIMESTAMP_LENGTH, PARENT_MAX_TEXT_LENGTH, PARENT_MAX_TOKEN_LENGTH, SESSION_END_REASONS, } from "./constants.js";
import { createRoutePrefixValidator, isRecord, readEnum, readSeq, readText, truncateText, } from "./primitives.js";
import { parseEmbeddedTheme } from "./theme.js";
export { isEmbeddedTheme } from "./theme.js";
export function isEmbeddedLocale(value) {
    return readEnum(value, LOCALES, MAX_ENUM_LENGTH) !== null;
}
function parseBusinessContext(value) {
    if (!isRecord(value)) {
        return null;
    }
    const systemCode = readText(value.systemCode, MAX_SYSTEM_CODE_LENGTH);
    const entityType = readText(value.entityType, MAX_ENTITY_TYPE_LENGTH);
    const entityId = readText(value.entityId, MAX_ENTITY_ID_LENGTH);
    if (!systemCode || !entityType || !entityId) {
        return null;
    }
    // displayHint 是不可信文本，只做长度限制并当纯文本渲染，绝不作为 HTML 注入页面（§12.3）。
    const displayHint = truncateText(value.displayHint, PARENT_MAX_TEXT_LENGTH);
    return { systemCode, entityType, entityId, ...(displayHint ? { displayHint } : {}) };
}
/**
 * 造一个父→子消息解析器。
 *
 * 做成工厂而不是直接导出函数，是因为路由前缀属于 iframe 侧的路由约定，
 * 不该被硬编码进协议包。
 */
export function createParentMessageParser(options) {
    const normalizePath = createRoutePrefixValidator(options.routePrefix, MAX_PATH_LENGTH);
    return function parseParentMessage(data) {
        if (!isRecord(data)) {
            return null;
        }
        switch (data.type) {
            case PARENT_MESSAGE.connect: {
                const frameNonce = readText(data.frameNonce, MAX_IDENTIFIER_LENGTH);
                const hostSessionId = readText(data.hostSessionId, MAX_IDENTIFIER_LENGTH);
                const protocolVersion = typeof data.protocolVersion === "number" ? data.protocolVersion : null;
                if (!frameNonce || !hostSessionId || protocolVersion === null) {
                    return null;
                }
                const initialPath = normalizePath(data.initialPath);
                const projectCode = data.projectCode === undefined ? undefined : readText(data.projectCode, MAX_PROJECT_CODE_LENGTH)?.trim();
                if (data.projectCode !== undefined && !projectCode) {
                    return null;
                }
                return {
                    type: PARENT_MESSAGE.connect,
                    protocolVersion,
                    frameNonce,
                    hostSessionId,
                    ...(projectCode ? { projectCode } : {}),
                    ...(initialPath ? { initialPath } : {}),
                };
            }
            case PARENT_MESSAGE.tokenUpdate: {
                const seq = readSeq(data.seq);
                const accessToken = readText(data.accessToken, PARENT_MAX_TOKEN_LENGTH);
                const expiresAt = readText(data.expiresAt, MAX_TIMESTAMP_LENGTH);
                if (seq === null || !accessToken || !expiresAt || Number.isNaN(Date.parse(expiresAt))) {
                    return null;
                }
                return { type: PARENT_MESSAGE.tokenUpdate, seq, accessToken, expiresAt };
            }
            case PARENT_MESSAGE.sessionEnd: {
                const reason = readEnum(data.reason, SESSION_END_REASONS, MAX_ENUM_LENGTH);
                return reason === null ? null : { type: PARENT_MESSAGE.sessionEnd, reason };
            }
            case PARENT_MESSAGE.contextUpdate: {
                const seq = readSeq(data.seq);
                if (seq === null) {
                    return null;
                }
                if (data.context === null) {
                    return { type: PARENT_MESSAGE.contextUpdate, seq, context: null };
                }
                const context = parseBusinessContext(data.context);
                return context ? { type: PARENT_MESSAGE.contextUpdate, seq, context } : null;
            }
            case PARENT_MESSAGE.navigationUpdate: {
                const seq = readSeq(data.seq);
                const path = normalizePath(data.path);
                return seq !== null && path ? { type: PARENT_MESSAGE.navigationUpdate, seq, path } : null;
            }
            case PARENT_MESSAGE.appearanceUpdate: {
                const seq = readSeq(data.seq);
                // 非法外观值整条丢弃并保持当前值，绝不回退到 `system`（§13、AR-30）。
                const theme = parseEmbeddedTheme(data.theme);
                if (seq === null || !isEmbeddedLocale(data.locale) || theme === null) {
                    return null;
                }
                return { type: PARENT_MESSAGE.appearanceUpdate, seq, locale: data.locale, theme };
            }
            default:
                return null;
        }
    };
}
//# sourceMappingURL=parse-parent.js.map